import React, { useState, useEffect, useContext, useMemo } from "react";
import axiosInstance from "api/axiosInstance";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { Grid, Divider } from "@mui/material";

import "./index.css";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import SignInLayout from "layouts/authentication/components/SignInLayout";
import { LanguageContext } from "../../../context/LanguageContext";
import { COLORS, TYPO } from "theme/tokens";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ loading: false });
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("forget_password", "authentication/forget-password/forget-password_lang");
  }, [language]);
  const LANGUAGE = useMemo(
    () => componentLanguageData.forget_password || { Main: {}, Alert: {} },
    [componentLanguageData]
  );

  useEffect(() => {
    if (status.loading) document.body.classList.add("loading");
    else document.body.classList.remove("loading");
    return () => document.body.classList.remove("loading");
  }, [status.loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true });

    try {
      const res = await axiosInstance.post("/auth/request-reset", { email });
      if (res.data.message === "Success_forget_001") {
        toast.success(LANGUAGE.Alert.Content1);
      } else {
        toast.warning(LANGUAGE.Alert.Content2);
      }
    } catch (err) {
      if (err.response?.data?.message === "Error_forget_001") {
        toast.error(LANGUAGE.Alert.Content3);
      } else {
        toast.error(LANGUAGE.Alert.Content4);
      }
    } finally {
      setStatus({ loading: false });
    }
  };

  // 依新版設計稿：bg #F6F6F6 / radius 6px / 內文 bodyM #000 / placeholder bodyM #C4C4C4
  const filledInputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: COLORS.grayLightest,
      borderRadius: "6px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused": {
        backgroundColor: COLORS.white,
        "& fieldset": { border: "1px solid", borderColor: COLORS.main },
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 8px 8px 8px",
      ...TYPO.bodyM,
      color: COLORS.black,
      "&::placeholder": { ...TYPO.bodyM, color: COLORS.grayMedian, opacity: 1 },
    },
  };

  const cardLogo =
    language === "zh-TW" || language === "zh-CN"
      ? "/images/logo/logo_2_1.png"
      : "/images/logo/logo_2_2.png";

  return (
    <SignInLayout>
      <Card
        sx={{
          width: "100%",
          maxWidth: 920,
          minHeight: { xs: "auto", md: 600 },
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <Grid container sx={{ minHeight: "inherit" }}>
          {/* === 左欄：品牌（白底 + 右框線 #E6E6E6 + logo 162×254 置中）=== */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: COLORS.white,
              borderRight: `1px solid ${COLORS.grayLighter}`,
              px: 10,
              py: 8,
            }}
          >
            <MDBox
              component="img"
              src={cardLogo}
              alt="MASQuant"
              sx={{ width: 162, height: 254, objectFit: "contain" }}
            />
          </Grid>

          {/* === 右欄：表單（白底 + 垂直水平置中 + padding 80px）=== */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              background: COLORS.white,
              p: { xs: 3, sm: 5, md: 8 },
            }}
          >
            <MDBox sx={{ width: "100%", maxWidth: 320 }}>
              {/* 手機版 logo */}
              <MDBox display={{ xs: "flex", md: "none" }} justifyContent="center" mb={3}>
                <MDBox component="img" src={cardLogo} alt="MASQuant" sx={{ maxHeight: 120, objectFit: "contain" }} />
              </MDBox>

              {/* 忘記密碼 — displayM / #000 / 500 */}
              <MDTypography sx={{ ...TYPO.displayM, color: COLORS.black, fontWeight: 500, mb: 3 }}>
                {LANGUAGE.Main.Content1}
              </MDTypography>

              <MDBox component="form" onSubmit={handleSubmit}>
                {/* Email* — bodyS / #000 / 400 */}
                <MDBox mb={2}>
                  <MDTypography sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 1 }}>
                    Email<span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                  </MDTypography>
                  <MDInput
                    type="email"
                    placeholder={LANGUAGE.Main.Content2}
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={filledInputSx}
                  />
                </MDBox>

                {/* 發送重設密碼 — 高 60 / radius 16 / 漸層 #F5C965→#F36D26 / title / #FFF / 500 */}
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={status.loading || !email}
                  sx={{
                    height: 60,
                    borderRadius: "16px",
                    background: COLORS.gradient,
                    boxShadow: "4px 4px 9px 0 rgba(222, 86, 15, 0.20)",
                    textTransform: "none",
                    ...TYPO.title,
                    color: `${COLORS.white} !important`,
                    fontWeight: 500,
                    "&:hover": {
                      background: COLORS.gradient,
                      boxShadow: "4px 4px 12px 0 rgba(222, 86, 15, 0.30)",
                    },
                    "&:disabled": {
                      background: COLORS.gradient,
                      color: `${COLORS.white} !important`,
                      opacity: 0.6,
                    },
                  }}
                >
                  {status.loading ? LANGUAGE.Alert.Content5 : LANGUAGE.Alert.Content6}
                </MDButton>

                {/* 分隔線 — 上下 24px + 1px #E6E6E6 */}
                <Divider sx={{ my: 3, borderColor: COLORS.grayLighter }} />

                {/* 想起密碼了嗎（bodyM/#000/400） + 回到登入（icon + title/#F36D26/500）*/}
                <MDBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                  <MDTypography sx={{ ...TYPO.bodyM, color: COLORS.black, fontWeight: 400 }}>
                    {LANGUAGE.Main.Content3}
                  </MDTypography>
                  <Link
                    to="/authentication/sign-in"
                    className="auth-underline"
                    style={{ display: "flex", alignItems: "center", gap: 6, textDecoration: "none" }}
                  >
                    <img
                      src="/images/icon/Vector-Left.png"
                      alt=""
                      style={{ width: 14, height: 14, objectFit: "contain" }}
                    />
                    <span style={{ ...TYPO.title, color: COLORS.main, fontWeight: 500 }}>
                      {LANGUAGE.Main.Content4}
                    </span>
                  </Link>
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </Card>

      {status.loading && (
        <div className="loading-overlay">
          <CircularProgress color="inherit" />
        </div>
      )}
    </SignInLayout>
  );
}
